import { useState, useEffect } from "react";
import { api, toast } from "../App";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Pencil, Trash2 } from "lucide-react";

export default function ErrorSettings({ user }) {
  const [errorTypes, setErrorTypes] = useState([]);
  const [errorMaster, setErrorMaster] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Error Type Dialog State
  const [typeDialogOpen, setTypeDialogOpen] = useState(false);
  const [editingType, setEditingType] = useState(null);
  const [typeFormData, setTypeFormData] = useState({
    name: "",
    weightage_points: ""
  });
  
  // Error Master Dialog State
  const [masterDialogOpen, setMasterDialogOpen] = useState(false);
  const [editingMaster, setEditingMaster] = useState(null);
  const [masterFormData, setMasterFormData] = useState({
    description: "",
    error_type_id: "",
    work_types: []
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [typesRes, masterRes] = await Promise.all([
        api.get("/error-types"),
        api.get("/error-master")
      ]);
      setErrorTypes(typesRes.data);
      setErrorMaster(masterRes.data);
    } catch (error) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  // Error Type Handlers
  const handleTypeSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingType) {
        await api.put(`/error-types/${editingType.id}`, typeFormData);
        toast.success("Error type updated successfully");
      } else {
        await api.post("/error-types", typeFormData);
        toast.success("Error type created successfully");
      }
      setTypeDialogOpen(false);
      resetTypeForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save error type");
    }
  };

  const handleEditType = (type) => {
    setEditingType(type);
    setTypeFormData({
      name: type.name,
      weightage_points: type.weightage_points.toString()
    });
    setTypeDialogOpen(true);
  };

  const handleDeleteType = async (typeId) => {
    if (!confirm("Are you sure you want to delete this error type?")) return;
    try {
      await api.delete(`/error-types/${typeId}`);
      toast.success("Error type deleted successfully");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete error type");
    }
  };

  const resetTypeForm = () => {
    setEditingType(null);
    setTypeFormData({ name: "", weightage_points: "" });
  };

  // Error Master Handlers
  const handleMasterSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingMaster) {
        await api.put(`/error-master/${editingMaster.id}`, masterFormData);
        toast.success("Master error updated successfully");
      } else {
        await api.post("/error-master", masterFormData);
        toast.success("Master error created successfully");
      }
      setMasterDialogOpen(false);
      resetMasterForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save master error");
    }
  };

  const handleEditMaster = (master) => {
    setEditingMaster(master);
    setMasterFormData({
      description: master.description,
      error_type_id: master.error_type_id,
      work_types: master.work_types || []
    });
    setMasterDialogOpen(true);
  };

  const handleDeleteMaster = async (masterId) => {
    if (!confirm("Are you sure you want to delete this master error?")) return;
    try {
      await api.delete(`/error-master/${masterId}`);
      toast.success("Master error deleted successfully");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete master error");
    }
  };

  const resetMasterForm = () => {
    setEditingMaster(null);
    setMasterFormData({ description: "", error_type_id: "", work_types: [] });
  };
  
  const toggleWorkType = (workType) => {
    const currentTypes = masterFormData.work_types || [];
    if (currentTypes.includes(workType)) {
      setMasterFormData({
        ...masterFormData,
        work_types: currentTypes.filter(t => t !== workType)
      });
    } else {
      setMasterFormData({
        ...masterFormData,
        work_types: [...currentTypes, workType]
      });
    }
  };

  return (
    <Layout user={user}>
      <div className="p-6 space-y-6">
        <div>
          <h1 className="text-md3-display-small">Error Settings</h1>
          <p className="text-md3-on-surface-variant">Manage error types and master error list</p>
        </div>

        {/* Error Types Section */}
        <Card className="bg-md3-surface-container shadow-md3-1 hover:shadow-md3-2 transition-shadow duration-200 rounded-md3-lg border-0">
          <CardHeader>
            <div className="flex justify-between items-center">
              <div>
                <CardTitle className="text-md3-title-large text-md3-on-surface ">Error Types</CardTitle>
                <CardDescription>Manage error types with weightage points</CardDescription>
              </div>
              <Button className="bg-md3-primary hover:bg-md3-primary/90 text-md3-on-primary shadow-md3-1 hover:shadow-md3-2 transition-all duration-200 rounded-md3-full" onClick={() => setTypeDialogOpen(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Add Error Type
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <p>Loading...</p>
            ) : (
              <Table className="bg-md3-surface-container rounded-md3-md">
                <TableHeader>
                  <TableRow>
                    <TableHead>Error Type Name</TableHead>
                    <TableHead>Weightage Points</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {errorTypes.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={3} className="text-center text-md3-on-surface-variant">
                        No error types yet. Create your first error type!
                      </TableCell>
                    </TableRow>
                  ) : (
                    errorTypes.map((type) => (
                      <TableRow key={type.id}>
                        <TableCell className="font-medium">{type.name}</TableCell>
                        <TableCell>{type.weightage_points}</TableCell>
                        <TableCell className="text-right space-x-2">
                          <Button variant="outline" className="border-md3-outline hover:bg-md3-surface-container-high transition-all duration-200 rounded-md3-full" size="sm"
                             onClick={() => handleEditType(type)}>
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button variant="destructive" className="bg-red-600 hover:bg-red-700 text-white shadow-md3-1 hover:shadow-md3-2 transition-all duration-200 rounded-md3-full" size="sm"
                             onClick={() => handleDeleteType(type.id)}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Error Master List Section */}
        <Card className="bg-md3-surface-container shadow-md3-1 hover:shadow-md3-2 transition-shadow duration-200 rounded-md3-lg border-0">
          <CardHeader>
            <div className="flex justify-between items-center">
              <div>
                <CardTitle className="text-md3-title-large text-md3-on-surface ">Error Master List</CardTitle>
                <CardDescription>Manage error descriptions with linked error types</CardDescription>
              </div>
              <Button className="bg-md3-primary hover:bg-md3-primary/90 text-md3-on-primary shadow-md3-1 hover:shadow-md3-2 transition-all duration-200 rounded-md3-full" onClick={() => setMasterDialogOpen(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Add Master Error
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <p>Loading...</p>
            ) : (
              <Table className="bg-md3-surface-container rounded-md3-md">
                <TableHeader>
                  <TableRow>
                    <TableHead>Error Description</TableHead>
                    <TableHead>Error Type</TableHead>
                    <TableHead>Work Type</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {errorMaster.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} className="text-center text-md3-on-surface-variant">
                        No master errors yet. Create your first master error!
                      </TableCell>
                    </TableRow>
                  ) : (
                    errorMaster.map((master) => (
                      <TableRow key={master.id}>
                        <TableCell className="font-medium">{master.description}</TableCell>
                        <TableCell>{master.error_type_name}</TableCell>
                        <TableCell>
                          {master.work_types && master.work_types.length > 0 ? (
                            <div className="flex flex-wrap gap-1">
                              {master.work_types.map((type) => (
                                <Badge key={type} variant="outline" className="text-xs">
                                  {type}
                                </Badge>
                              ))}
                            </div>
                          ) : (
                            <span className="text-gray-400">N/A</span>
                          )}
                        </TableCell>
                        <TableCell className="text-right space-x-2">
                          <Button variant="outline" className="border-md3-outline hover:bg-md3-surface-container-high transition-all duration-200 rounded-md3-full" size="sm"
                             onClick={() => handleEditMaster(master)}>
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button variant="destructive" className="bg-red-600 hover:bg-red-700 text-white shadow-md3-1 hover:shadow-md3-2 transition-all duration-200 rounded-md3-full" size="sm"
                             onClick={() => handleDeleteMaster(master.id)}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Error Type Dialog */}
        <Dialog open={typeDialogOpen} onOpenChange={(open) => {
          setTypeDialogOpen(open);
          if (!open) resetTypeForm();
        }}>
          <DialogContent className="bg-white">
            <DialogHeader>
              <DialogTitle className="text-md3-headline-small text-md3-on-surface font-semibold ">{editingType ? "Edit Error Type" : "Add Error Type"}</DialogTitle>
              <DialogDescription className="text-md3-body-medium text-gray-600">
                {editingType ? "Update the error type details" : "Create a new error type with weightage"}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleTypeSubmit} className="space-y-6">
              <div>
                <Label htmlFor="type_name" className="text-md3-label-large text-gray-700 font-medium">Error Type Name*</Label>
                <Input className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" id="type_name"
                  value={typeFormData.name}
                  onChange={(e) => setTypeFormData({ ...typeFormData, name: e.target.value })}
                  required
                  placeholder="e.g., Missing Bolt Holes"
                />
              </div>
              <div>
                <Label htmlFor="weightage" className="text-md3-label-large text-gray-700 font-medium">Weightage Points*</Label>
                <Input className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" id="weightage"
                  type="number"
                  step="0.1"
                  value={typeFormData.weightage_points}
                  onChange={(e) => setTypeFormData({ ...typeFormData, weightage_points: e.target.value })}
                  required
                  placeholder="e.g., 5.0"
                />
              </div>
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" className="border-md3-outline hover:bg-md3-surface-container-high transition-all duration-200 rounded-md3-full" onClick={() => {
                  setTypeDialogOpen(false);
                  resetTypeForm();
                }}>
                  Cancel
                </Button>
                <Button type="submit" className="bg-md3-primary hover:bg-md3-primary/90 text-md3-on-primary shadow-md3-1 hover:shadow-md3-2 transition-all duration-200 rounded-md3-full" >
                  {editingType ? "Update" : "Create"}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Error Master Dialog */}
        <Dialog open={masterDialogOpen} onOpenChange={(open) => {
          setMasterDialogOpen(open);
          if (!open) resetMasterForm();
        }}>
          <DialogContent className="bg-white">
            <DialogHeader>
              <DialogTitle className="text-md3-headline-small text-md3-on-surface font-semibold ">{editingMaster ? "Edit Master Error" : "Add Master Error"}</DialogTitle>
              <DialogDescription className="text-md3-body-medium text-gray-600">
                {editingMaster ? "Update the master error details" : "Create a new master error"}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleMasterSubmit} className="space-y-6">
              <div>
                <Label htmlFor="description" className="text-md3-label-large text-gray-700 font-medium">Error Description*</Label>
                <Input className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" id="description"
                  value={masterFormData.description}
                  onChange={(e) => setMasterFormData({ ...masterFormData, description: e.target.value })}
                  required
                  placeholder="e.g., Bolt holes missing in base plate"
                />
              </div>
              <div>
                <Label htmlFor="error_type" className="text-md3-label-large text-gray-700 font-medium">Error Type*</Label>
                <Select
                  value={masterFormData.error_type_id}
                  onValueChange={(value) => setMasterFormData({ ...masterFormData, error_type_id: value })}
                  required
                >
                  <SelectTrigger id="error_type">
                    <SelectValue placeholder="Select error type" />
                  </SelectTrigger>
                  <SelectContent>
                    {errorTypes.map((type) => (
                      <SelectItem key={type.id} value={type.id}>
                        {type.name} ({type.weightage_points} points)
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Work Types (Select all that apply)</Label>
                <div className="mt-2 space-y-2">
                  {["Shop", "Erection", "Modelling", "Checking", "Estimation", "Client Feedback"].map((workType) => (
                    <div key={workType} className="flex items-center">
                      <input
                        type="checkbox"
                        id={`work_type_${workType}`}
                        checked={(masterFormData.work_types || []).includes(workType)}
                        onChange={() => toggleWorkType(workType)}
                        className="mr-2 h-4 w-4"
                      />
                      <label htmlFor={`work_type_${workType}`} className="cursor-pointer">
                        {workType}
                      </label>
                    </div>
                  ))}
                </div>
              </div>
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" className="border-md3-outline hover:bg-md3-surface-container-high transition-all duration-200 rounded-md3-full" onClick={() => {
                  setMasterDialogOpen(false);
                  resetMasterForm();
                }}>
                  Cancel
                </Button>
                <Button type="submit" className="bg-md3-primary hover:bg-md3-primary/90 text-md3-on-primary shadow-md3-1 hover:shadow-md3-2 transition-all duration-200 rounded-md3-full" >
                  {editingMaster ? "Update" : "Create"}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
